import { useGoogleFont, iconFor } from './shared';

/* "Editorial" direction: asymmetric hero, numbered hairline-divided service
   list, pull-quote testimonial, generous whitespace. Sharp-ish corners and
   a structured, magazine-like rhythm -- the calmer of the two directions. */
const TemplateEditorial = ({ brandKit, content }) => {
  const colors = brandKit?.colors || {};
  const bg = colors.background?.hex || '#FFFFFF';
  const fg = colors.foreground?.hex || '#1A1A1A';
  const primary = colors.primary?.hex || '#1B3A5C';
  const accent = colors.accent?.hex || primary;
  const headingFont = brandKit?.typography?.heading_font || 'Playfair Display';
  const bodyFont = brandKit?.typography?.body_font || 'Inter';
  const logo = brandKit?.logos?.primary;
  const businessName = brandKit?.business_name || 'Your Business';

  useGoogleFont(headingFont);
  useGoogleFont(bodyFont);

  const services = content?.services?.length ? content.services : [];
  const gallery = content?.gallery?.filter((g) => g.image_url) || [];
  const stats = content?.stats?.filter((s) => s.value) || [];
  const processSteps = content?.process_steps?.filter((p) => p.title) || [];
  const team = content?.team?.filter((t) => t.name) || [];
  const faq = content?.faq?.filter((f) => f.question) || [];
  const heading = { fontFamily: `'${headingFont}', serif` };

  return (
    <div style={{ background: bg, color: fg, fontFamily: `'${bodyFont}', sans-serif` }} className="w-full">
      {/* Nav */}
      <div className="flex items-center justify-between px-8 py-5 border-b" style={{ borderColor: `${fg}1a` }}>
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt={businessName} className="h-7 w-7 object-contain" />
          ) : (
            <div className="h-7 w-7 rounded-sm" style={{ background: primary }} />
          )}
          <span style={heading} className="text-lg font-semibold">{businessName}</span>
        </div>
        <div className="hidden sm:flex items-center gap-6 text-sm opacity-70">
          <span>Services</span><span>About</span><span>Contact</span>
        </div>
      </div>

      {/* Hero -- asymmetric: copy left, real photography right */}
      <div className={`px-8 pt-16 pb-14 grid grid-cols-1 ${content?.hero_image_url ? 'lg:grid-cols-[1.1fr_0.9fr] lg:items-center gap-10' : ''}`}>
        <div className={content?.hero_image_url ? '' : 'max-w-3xl'}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-5" style={{ color: primary }}>
            <span className="w-6 h-px" style={{ background: primary }} />
            {content?.eyebrow || 'Studio'}
          </div>
          <h1 style={heading} className="text-5xl leading-[1.1] font-semibold mb-6">
            {content?.headline || 'A headline that says exactly who you are.'}
          </h1>
          <p className="text-lg opacity-75 mb-8 max-w-xl">
            {content?.subheadline || 'A supporting line that explains the offer clearly.'}
          </p>
          <button className="px-6 py-3 text-sm font-medium" style={{ background: primary, color: bg }}>
            {content?.cta_button_text || 'Get Started'}
          </button>
        </div>
        {content?.hero_image_url && (
          <div className="aspect-[4/5] overflow-hidden">
            <img src={content.hero_image_url} alt={businessName} className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Services -- numbered, hairline-divided */}
      <div className="border-t" style={{ borderColor: `${fg}1a` }}>
        {services.map((s, i) => {
          const Icon = iconFor(s.icon_hint);
          return (
            <div
              key={i}
              className="flex items-start gap-6 px-8 py-8 border-b"
              style={{ borderColor: `${fg}1a` }}
            >
              <span className="text-sm font-mono opacity-40 pt-1 w-8 shrink-0">{String(i + 1).padStart(2, '0')}</span>
              <Icon className="w-5 h-5 mt-1 shrink-0" style={{ color: accent }} />
              <div>
                <h3 style={heading} className="text-xl font-medium mb-1.5">{s.title}</h3>
                <p className="opacity-70 text-sm max-w-md">{s.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gallery -- optional, only for visually tangible businesses */}
      {gallery.length > 0 && (
        <div className="px-8 py-16 border-t" style={{ borderColor: `${fg}1a` }}>
          <h2 style={heading} className="text-2xl font-semibold mb-8">Gallery</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <figure key={i}>
                <div className="aspect-square overflow-hidden mb-2">
                  <img src={g.image_url} alt={g.caption} className="w-full h-full object-cover" />
                </div>
                {g.caption && <figcaption className="text-xs opacity-60">{g.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      )}

      {/* Stats -- optional */}
      {stats.length > 0 && (
        <div className="border-t grid grid-cols-2 sm:grid-cols-4" style={{ borderColor: `${fg}1a` }}>
          {stats.map((s, i) => (
            <div key={i} className="px-6 py-10 text-center border-r last:border-r-0" style={{ borderColor: `${fg}1a` }}>
              <p className="text-3xl font-semibold mb-1" style={{ ...heading, color: primary }}>{s.value}</p>
              <p className="text-xs uppercase tracking-wide opacity-60">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Process -- optional */}
      {processSteps.length > 0 && (
        <div className="px-8 py-16 border-t" style={{ borderColor: `${fg}1a` }}>
          <h2 style={heading} className="text-2xl font-semibold mb-8">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {processSteps.map((p, i) => (
              <div key={i}>
                <span className="text-sm font-mono opacity-40">{String(i + 1).padStart(2, '0')}</span>
                <h3 style={heading} className="text-lg font-medium mt-1 mb-1.5">{p.title}</h3>
                <p className="opacity-70 text-sm">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* About + testimonial */}
      <div className="px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 border-t" style={{ borderColor: `${fg}1a` }}>
        <div>
          <h2 style={heading} className="text-2xl font-semibold mb-4">
            {content?.about_title || 'About'}
          </h2>
          <p className="opacity-75 leading-relaxed">{content?.about_body}</p>
        </div>
        <div className="pl-6 border-l-2" style={{ borderColor: accent }}>
          <p style={heading} className="text-xl italic leading-snug mb-3">
            &ldquo;{content?.testimonial_quote}&rdquo;
          </p>
          <p className="text-sm opacity-60">{content?.testimonial_author}</p>
        </div>
      </div>

      {/* Team -- optional */}
      {team.length > 0 && (
        <div className="px-8 py-16 border-t" style={{ borderColor: `${fg}1a` }}>
          <h2 style={heading} className="text-2xl font-semibold mb-8">The Team</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {team.map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                  style={{ background: `${primary}14`, color: primary }}
                >
                  {t.name.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{t.name}</p>
                  <p className="text-xs opacity-60 mb-1">{t.role}</p>
                  <p className="text-sm opacity-70">{t.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ -- optional */}
      {faq.length > 0 && (
        <div className="px-8 py-16 border-t" style={{ borderColor: `${fg}1a` }}>
          <h2 style={heading} className="text-2xl font-semibold mb-8">Frequently Asked</h2>
          <div className="max-w-2xl">
            {faq.map((f, i) => (
              <div key={i} className="py-5 border-b" style={{ borderColor: `${fg}1a` }}>
                <p className="font-medium mb-1.5">{f.question}</p>
                <p className="opacity-70 text-sm">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA band */}
      <div className="px-8 py-14 text-center" style={{ background: primary, color: bg }}>
        <h2 style={heading} className="text-3xl font-semibold mb-3">
          {content?.cta_headline || 'Ready to start?'}
        </h2>
        <p className="opacity-85 mb-6 max-w-md mx-auto">{content?.cta_body}</p>
        <button className="px-6 py-3 text-sm font-medium" style={{ background: bg, color: primary }}>
          {content?.cta_button_text || 'Get Started'}
        </button>
      </div>

      {/* Footer */}
      <div className="px-8 py-6 flex items-center justify-between text-xs opacity-50">
        <span>&copy; {new Date().getFullYear()} {businessName}</span>
        <span>{content?.footer_tagline}</span>
      </div>
    </div>
  );
};

export default TemplateEditorial;
